import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, MoreHorizontal, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
    getReadingHistory, getTodayReadings, markReadingInError,
    type Reading, type ReadingCode, type ReadingType, type TodayReadings,
} from "@/lib/api";
import { flagFor, flagTone, fmtDate, fmtNum, fmtTime } from "@/lib/vitals";
import RecordVitalsDialog from "./RecordVitalsDialog";
import Sparkline from "./Sparkline";

interface Props {
    patientId: string;
    patientName: string;
    canRecord: boolean;
    /** Patient's age in months — under 18 the H/L flags use paediatric ranges */
    ageMonths?: number | null;
}

const SOURCE_LABEL: Record<Reading["source"], string> = {
    triage: "Triage", doctor: "Doctor", prescription: "Prescription",
    lab_report: "Lab report", device: "Device", patient: "Patient",
};
const ERROR_REASONS = ["Wrong patient", "Typing mistake", "Faulty measurement"];

// Trend tiles; BP shows systolic and diastolic together
const TREND_CODES: ("bp" | Exclude<ReadingCode, "bp_systolic" | "bp_diastolic">)[] = ["bp", "pulse", "temp", "spo2", "weight", "height", "rbs"];
const COLUMN_LABEL: Record<(typeof TREND_CODES)[number], string> = {
    bp: "BP", pulse: "Pulse", temp: "Temp", spo2: "SpO₂", weight: "Wt", height: "Ht", rbs: "RBS",
};

/** Patient page: latest value and trend for each vital, and every reading with who recorded it. */
export default function PatientVitalsTab({ patientId, patientName, canRecord, ageMonths = null }: Props) {
    const [readings, setReadings] = useState<Reading[]>([]);
    const [types, setTypes] = useState<ReadingType[]>([]);
    const [loading, setLoading] = useState(true);
    const [recordOpen, setRecordOpen] = useState(false);
    const [today, setToday] = useState<TodayReadings | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        getReadingHistory(patientId)
            .then(r => { setReadings(r.readings); setTypes(r.types); })
            .catch(() => toast({ title: "Couldn't load vitals", variant: "destructive" }))
            .finally(() => setLoading(false));
    }, [patientId]);
    useEffect(load, [load]);

    const openRecord = () => {
        getTodayReadings(patientId).then(setToday).catch(() => setToday(null));
        setRecordOpen(true);
    };

    const finals = useMemo(() => readings.filter(r => r.status === "final"), [readings]);
    const byCode = useMemo(() => {
        const m: Partial<Record<ReadingCode, Reading[]>> = {};
        // oldest → newest for trends
        for (const r of [...finals].reverse()) (m[r.code] ??= []).push(r);
        return m;
    }, [finals]);
    const typeOf = (code: ReadingCode) => types.find(t => t.code === code);

    // Rows: readings entered together, newest first
    const groups = useMemo(() => {
        const order: string[] = [];
        const map = new Map<string, Reading[]>();
        for (const r of readings) {
            if (!map.has(r.readingGroup)) { map.set(r.readingGroup, []); order.push(r.readingGroup); }
            map.get(r.readingGroup)!.push(r);
        }
        return order.map(id => map.get(id)!);
    }, [readings]);

    const markGroupInError = async (group: Reading[], reason: string) => {
        setBusy(true);
        try {
            const done = new Set<string>();
            for (const r of group.filter(g => g.status === "final")) {
                if (done.has(r.id)) continue;
                const res = await markReadingInError(r.id, reason);
                res.ids.forEach(id => done.add(id));
            }
            toast({ title: "Marked as entered in error", description: "The readings stay in the record, struck through." });
            load();
        } catch (err: any) {
            toast({ title: "Not corrected", description: err?.message ?? "Please try again", variant: "destructive" });
        } finally {
            setBusy(false);
        }
    };

    const tiles = TREND_CODES.map(code => {
        if (code === "bp") {
            const sys = byCode.bp_systolic ?? [], dia = byCode.bp_diastolic ?? [];
            if (!sys.length) return null;
            const last = sys[sys.length - 1];
            const lastDia = dia[dia.length - 1];
            return {
                code, label: "Blood pressure", unit: "mmHg", last, count: sys.length,
                value: `${fmtNum(last.value)}/${lastDia ? fmtNum(lastDia.value) : "–"}`,
                flag: flagFor(last, ageMonths) ?? flagFor(lastDia, ageMonths),
                series: [sys.slice(-12).map(r => r.value), dia.slice(-12).map(r => r.value)],
                low: null, high: null,
            };
        }
        const list = byCode[code] ?? [];
        if (!list.length) return null;
        const last = list[list.length - 1];
        const t = typeOf(code);
        return {
            code, label: t?.name ?? code, unit: last.unit, last, count: list.length,
            value: fmtNum(last.value), flag: flagFor(last, ageMonths),
            series: [list.slice(-12).map(r => r.value)], low: t?.normalLow, high: t?.normalHigh,
        };
    }).filter(Boolean) as any[];

    const cellValue = (group: Reading[], code: "bp" | ReadingCode) => {
        if (code === "bp") {
            const s = group.find(r => r.code === "bp_systolic"), d = group.find(r => r.code === "bp_diastolic");
            return s && d ? { text: `${fmtNum(s.value)}/${fmtNum(d.value)}`, flag: flagFor(s, ageMonths) ?? flagFor(d, ageMonths) } : null;
        }
        const r = group.find(x => x.code === code);
        return r ? { text: fmtNum(r.value), flag: flagFor(r, ageMonths) } : null;
    };

    // Columns: only the vitals this patient has readings of
    const columns = TREND_CODES.filter(code => groups.some(g => cellValue(g, code)));
    const recordedBy = (r: Reading) => (r.recordedBy ? `${r.recordedBy} · ${SOURCE_LABEL[r.source]}` : SOURCE_LABEL[r.source]);
    const struckNote = (r: Reading) =>
        `${r.status === "superseded" ? "Replaced" : "Entered in error"}${r.statusReason ? `: ${r.statusReason}` : ""}${r.statusBy ? ` (${r.statusBy})` : ""}`;

    // "Entered in error" sits behind the row's menu: the readings stay in the record, struck through
    const errorMenu = (group: Reading[]) => (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground" disabled={busy}
                    aria-label={`More actions for the readings of ${fmtDate(group[0].effectiveAt)} ${fmtTime(group[0].effectiveAt)}`}>
                    <MoreHorizontal className="w-4 h-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Mark as entered in error</DropdownMenuLabel>
                {ERROR_REASONS.map(reason => (
                    <DropdownMenuItem key={reason} onSelect={() => markGroupInError(group, reason)}>{reason}</DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );

    const recordButton = canRecord && (
        <Button size="sm" className="gap-1.5" onClick={openRecord}>
            <Plus className="w-4 h-4" /> Record vitals
        </Button>
    );

    return (
        <div className="space-y-5">
            {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            ) : !readings.length ? (
                <div className="rounded-lg border border-border bg-card px-6 py-12 text-center">
                    <p className="text-sm font-semibold text-foreground">No vitals recorded yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Vitals from prescriptions and triage appear here.</p>
                    {recordButton && <div className="mt-4 flex justify-center">{recordButton}</div>}
                </div>
            ) : (
                <>
                    {/* Latest value and trend per vital */}
                    <section aria-label="Latest vitals" className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                            <h2 className="text-sm font-semibold text-foreground">Latest</h2>
                            {recordButton}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fill,minmax(168px,1fr))] gap-3">
                            {tiles.map(tile => (
                                <div key={tile.code} className="rounded-lg border border-border bg-card px-4 py-3 min-w-0">
                                    <p className="text-xs text-muted-foreground truncate">{tile.label}</p>
                                    <p className="mt-0.5 flex items-baseline gap-1">
                                        <span className={cn("font-mono text-xl font-semibold tabular-nums text-foreground", flagTone(tile.flag, tile.code))}>
                                            {tile.value}
                                        </span>
                                        {tile.flag && <span className={cn("text-xs font-semibold", flagTone(tile.flag, tile.code))}>{tile.flag}</span>}
                                        <span className="text-xs text-muted-foreground">{tile.unit}</span>
                                    </p>
                                    {/* The trend needs two readings; the line keeps its height so tiles stay level */}
                                    <div className="h-[34px] mt-1">
                                        <Sparkline series={tile.series} low={tile.low} high={tile.high} />
                                    </div>
                                    <p className="mt-1 text-xs text-muted-foreground truncate tabular-nums">
                                        {fmtDate(tile.last.effectiveAt)} · {tile.count} reading{tile.count === 1 ? "" : "s"}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Every reading, grouped as entered */}
                    <section aria-label="All readings" className="rounded-lg border border-border bg-card overflow-hidden">
                        <div className="flex items-center h-11 px-4 border-b border-border">
                            <h2 className="text-sm font-semibold text-foreground">
                                All readings <span className="ml-1 font-normal text-muted-foreground tabular-nums">{groups.length}</span>
                            </h2>
                        </div>

                        {/* Tablets and up: one row per set of readings */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="h-8 bg-muted/40 border-b border-border text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                                        <th className="text-left font-medium px-4">When</th>
                                        {columns.map(code => <th key={code} className="text-right font-medium px-3">{COLUMN_LABEL[code]}</th>)}
                                        <th className="text-left font-medium px-4">Recorded by</th>
                                        <th className="w-12" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {groups.map(group => {
                                        const first = group[0];
                                        const struck = group.every(r => r.status !== "final");
                                        const correctable = canRecord && !struck && first.source !== "prescription";
                                        return (
                                            <tr key={first.readingGroup} className={cn("h-10 border-b border-divider last:border-b-0", struck && "text-muted-foreground")}>
                                                <td className="px-4 whitespace-nowrap tabular-nums">
                                                    <span className={cn(!struck && "font-medium text-foreground")}>{fmtDate(first.effectiveAt)}</span>
                                                    <span className="text-muted-foreground"> {fmtTime(first.effectiveAt)}</span>
                                                </td>
                                                {columns.map(code => {
                                                    const v = cellValue(group, code);
                                                    return (
                                                        <td key={code} className={cn("px-3 text-right font-mono tabular-nums",
                                                            struck && "line-through",
                                                            !struck && v?.flag && `${flagTone(v.flag, code)} font-semibold`)}>
                                                            {v ? <>{v.text}{!struck && v.flag && <span className="ml-0.5 text-2xs font-sans">{v.flag}</span>}</> : <span className="text-muted-foreground/50">—</span>}
                                                        </td>
                                                    );
                                                })}
                                                <td className="px-4 py-1.5 text-[13px]">
                                                    <span className="text-muted-foreground">{recordedBy(first)}</span>
                                                    {struck && <span className="block text-xs text-destructive">{struckNote(first)}</span>}
                                                </td>
                                                <td className="pr-2 text-right">{correctable && errorMenu(group)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Phones: each set of readings as two lines, no sideways scrolling */}
                        <ul className="md:hidden divide-y divide-divider">
                            {groups.map(group => {
                                const first = group[0];
                                const struck = group.every(r => r.status !== "final");
                                const correctable = canRecord && !struck && first.source !== "prescription";
                                return (
                                    <li key={first.readingGroup} className={cn("flex items-start gap-2 px-4 py-2.5", struck && "text-muted-foreground")}>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm tabular-nums">
                                                <span className={cn(!struck && "font-medium text-foreground")}>{fmtDate(first.effectiveAt)}</span>
                                                <span className="text-muted-foreground"> {fmtTime(first.effectiveAt)} · {recordedBy(first)}</span>
                                            </p>
                                            <p className={cn("mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-sm", struck && "line-through")}>
                                                {columns.map(code => {
                                                    const v = cellValue(group, code);
                                                    if (!v) return null;
                                                    return (
                                                        <span key={code} className="whitespace-nowrap">
                                                            <span className="text-xs text-muted-foreground">{COLUMN_LABEL[code]} </span>
                                                            <span className={cn("font-mono tabular-nums", !struck && v.flag && `${flagTone(v.flag, code)} font-semibold`)}>
                                                                {v.text}{!struck && v.flag && <span className="ml-0.5 text-2xs font-sans">{v.flag}</span>}
                                                            </span>
                                                        </span>
                                                    );
                                                })}
                                            </p>
                                            {struck && <p className="mt-0.5 text-xs text-destructive">{struckNote(first)}</p>}
                                        </div>
                                        {correctable && errorMenu(group)}
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                </>
            )}

            <RecordVitalsDialog
                open={recordOpen}
                onOpenChange={setRecordOpen}
                patientId={patientId}
                patientName={patientName}
                today={today}
                onSaved={() => load()}
            />
        </div>
    );
}
